import { action, computed, makeObservable, observable } from 'mobx';

import {
  getLiveEvents,
  MAX_CARRIED_CONSUMERS,
  type LiveEvent,
  type LiveEventSubscription,
  type LiveEventWaitingInstance,
} from 'api/events/endpoints/getLiveEvents';
import { raiseEvent } from 'api/events/endpoints/raiseEvent';
import { toError } from 'helpers/toError';

import type { LiveEventsModel } from './LiveEventsModel';
import { acceptDegraded } from 'validation';

/**
 * Whether `candidate` is a later read of an event than `held`.
 *
 * The whole freshness scheme rests on this one comparison, so it is stated
 * once: a card uses it against the page's read and against its own previous
 * read, and both have to answer the same way.
 *
 * Compared as instants, not as strings. The two agree today only because
 * node-postgres parses timestamptz into a Date and res.json() serialises it as
 * ISO-8601 UTC; a type parser returning the raw `+02:00` form would make string
 * order diverge from chronological order silently, and in the direction that
 * lets a stale read win.
 */
const isNewer = (candidate: LiveEvent, held: LiveEvent): boolean => Date.parse(candidate.as_of) > Date.parse(held.as_of);

/**
 * One Live Events card.
 *
 * Holds no copy of its event. The page read already has one, so a second would
 * be duplicate state, and reconciling the two is what forces a model into
 * writing from a computed. Instead the card keeps at most one thing the page
 * does not have — the answer to its OWN refresh — and {@link _event} picks
 * whichever of the two is fresher by the `as_of` the database stamped on them.
 * Every property below is derived from that one choice.
 *
 * Because the comparison is on the data rather than on request order, the
 * awkward cases settle themselves: a local read that lands after a page reload
 * is either genuinely newer (and should win) or genuinely older (and is
 * ignored, for ever, since it can never become fresher again).
 *
 * Page-wide state is not duplicated either. Collapse and focus live on the
 * owner as the single source of truth and are read back through `_owner`.
 */
export class LiveEventCardModel {
  private readonly _eventId: number;
  private readonly _owner: LiveEventsModel;

  private _busy = false;
  private _error: string | null = null;
  /** This card's own last read, if it has made one. */
  private _local: LiveEvent | null = null;

  constructor(owner: LiveEventsModel, eventId: number) {
    this._owner = owner;
    this._eventId = eventId;

    makeObservable<LiveEventCardModel, '_busy' | '_error' | '_local' | '_event' | '_setLocal' | '_setPending'>(this, {
      _busy: observable,
      _error: observable,
      _local: observable.ref,
      _event: computed,
      collapsed: computed,
      consumers: computed,
      consumersTruncated: computed,
      focused: computed,
      forceVisible: computed,
      subscriptions: computed,
      surplusCount: computed,
      waitingCount: computed,
      _setLocal: action,
      _setPending: action,
    });
  }

  // ── Accessors ─────────────────────────────────────────────────────────────

  /** A raise or refresh is in flight. */
  get busy(): boolean {
    return this._busy;
  }

  /** Derived from the owner, which holds the collapse state for the whole page. */
  get collapsed(): boolean {
    return this._owner.isCollapsed(this._eventId);
  }

  /**
   * The node instances this event has as consumers, those still waiting first.
   *
   * Consumers a raise has already served stay in the list with `is_waiting`
   * false and their current status — that is how the operator sees what the
   * raise did. The backend does that merge (see `include_node_instance_ids`),
   * which is why the served rows stay live: their status is re-read every
   * time, not frozen at the moment they stopped waiting.
   */
  get consumers(): readonly LiveEventWaitingInstance[] {
    return this._event?.waiting_instances ?? [];
  }

  /**
   * The event has more consumers than {@link consumers} lists.
   *
   * The card says so rather than showing a short list silently, which would
   * read as the whole of the demand. {@link waitingCount} stays true regardless.
   */
  get consumersTruncated(): boolean {
    return this._event?.consumers_truncated ?? false;
  }

  get description(): string | null {
    return this._event?.description ?? null;
  }

  get error(): string | null {
    return this._error;
  }

  get eventId(): number {
    return this._eventId;
  }

  get eventType(): string {
    return this._event?.type ?? '';
  }

  /** Derived from the owner, which holds the focused event id. */
  get focused(): boolean {
    return this._owner.focusEventId === this._eventId;
  }

  /**
   * On the page despite not being out of balance — the focused card, or one
   * whose own raise or discard emptied it.
   */
  get forceVisible(): boolean {
    return this._event !== undefined && !this._event.qualifies;
  }

  /** False once the event has left the page entirely; nothing should render it. */
  get isPresent(): boolean {
    return this._event !== undefined;
  }

  get name(): string {
    return this._event?.name ?? '';
  }

  /** Definitions a raise would auto-submit. Empty when the page is not counting them. */
  get subscriptions(): readonly LiveEventSubscription[] {
    return this._event?.auto_submit_subscriptions ?? [];
  }

  /** Unconsumed occurrences. */
  get surplusCount(): number {
    return this._event?.surplus_count ?? 0;
  }

  /** Consumers actually blocked on this event — not the length of {@link consumers}. */
  get waitingCount(): number {
    return this._event?.waiting_count ?? 0;
  }

  get zoneName(): string {
    return this._event?.zone_name ?? '';
  }

  /**
   * The freshest read of this event available, or undefined once it has left
   * the page and this card has never read it itself.
   *
   * `as_of` comes from the database, so this is a question about the data
   * rather than about which request was issued first.
   */
  private get _event(): LiveEvent | undefined {
    const fromPage = this._owner.eventById(this._eventId);
    if (this._local === null) {
      return fromPage;
    }
    if (fromPage === undefined) {
      return this._local;
    }
    return isNewer(this._local, fromPage) ? this._local : fromPage;
  }

  // ── Methods ───────────────────────────────────────────────────────────────

  /**
   * Raise this event, then re-read the card. Non-destructive: the card stays
   * whether or not it still qualifies, keeps its position, and keeps the
   * consumers already on screen so the operator can see which the raise served.
   */
  raise = async (): Promise<void> => {
    const event = this._event;
    if (event === undefined) {
      return;
    }

    this._setPending();
    try {
      await raiseEvent({ zoneName: event.zone_name, eventName: event.name, eventType: event.type });
      await this.refresh();
    } catch (error) {
      this._setLocal(null, toError(error).message);
    }
  };

  /** Re-read this card in place. Never changes which cards are on the page. */
  refresh = async (): Promise<void> => {
    this._setPending();
    try {
      const response = acceptDegraded(
        await getLiveEvents({
          event_ids: [this._eventId],
          include_auto_submit: this._owner.includeAutoSubmit,
          // The consumers already on screen, so the backend returns the ones a
          // raise has served alongside those still waiting. Taken from the
          // current view rather than accumulated separately: the response echoes
          // them back, so the list carries itself forward.
          //
          // Capped because the endpoint rejects a longer list than it returns.
          // Uncapped, an event with more consumers than the bound would load once
          // and then fail every Refresh and Raise with a 400 -- the card would
          // stop working rather than degrade.
          include_node_instance_ids: this.consumers.slice(0, MAX_CARRIED_CONSUMERS).map((consumer) => consumer.node_instance_id),
        })
      );
      const fresh = response.events.find((event) => event.event_id === this._eventId);
      if (fresh === undefined) {
        // Deleted since the page loaded. Leave what is on screen, but say so
        // rather than letting stale counts look live.
        this._setLocal(null, 'This event no longer exists.');
        return;
      }
      this._setLocal(fresh, null);
    } catch (error) {
      this._setLocal(null, toError(error).message);
    }
  };

  toggleCollapsed = (): void => {
    this._owner.toggleCollapsed(this._eventId);
  };

  // ── Private ───────────────────────────────────────────────────────────────

  /**
   * Record the outcome of this card's own read.
   *
   * Kept only when it is newer than the local already held — the same question
   * {@link _event} asks of the page's read. Two of this card's own reads can be
   * in flight at once (a discard fires a refresh that `busy` does not gate), and
   * if the older resolved last it would win permanently: both are newer than
   * the page's answer, so nothing would ever displace it.
   *
   * A failure leaves any previous local in place — it is still the freshest
   * thing this card knows — and only adds the message. The error is deliberately
   * NOT cleared by a page reload: it describes this card's last action, so it
   * stands until the operator retries.
   */
  private _setLocal = (event: LiveEvent | null, error: string | null): void => {
    if (event !== null && (this._local === null || isNewer(event, this._local))) {
      this._local = event;
    }
    this._busy = false;
    this._error = error;
  };

  private _setPending = (): void => {
    this._busy = true;
    this._error = null;
  };
}
